import { Spin, Tag } from 'antd'
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useUserMedia } from '@/features/advisory-ai/hooks'
import { isVoiceCloneProcessing } from '@/features/advisory-ai/api'
import { AvatarSettingsPanel } from './AvatarSettingsPanel'
import { VoiceSettingsPanel } from './VoiceSettingsPanel'

// Status tag for the avatar/voice section headers, driven by the combined media query.
function MediaStatusTag({ has, status }: { has: boolean; status: string | null }) {
  if (!has) return <Tag color="default">Not set up</Tag>
  if (isVoiceCloneProcessing(status ?? undefined)) {
    return (
      <Tag color="gold" icon={<Spin size="small" />}>
        Processing
      </Tag>
    )
  }
  if (/error|fail/i.test(String(status ?? ''))) return <Tag color="red">{status}</Tag>
  return (
    <Tag color="green" icon={<CheckCircleOutlined />}>
      Ready
    </Tag>
  )
}

// Avatar and Voice live under a single settings tab. Both are required before a
// Video Avatar Summary can be generated for a meeting.
export function AvatarVoicePanel() {
  const { data: media } = useUserMedia()

  return (
    <div className="space-y-6">
      <div
        className="flex items-start gap-3 rounded-lg border p-4"
        style={{ borderColor: '#a8e4f3', background: 'var(--feature-primary-soft)' }}
      >
        <InfoCircleOutlined className="text-lg mt-0.5" style={{ color: 'var(--feature-primary-strong)' }} />
        <p className="text-sm text-gray-700">
          <span className="font-semibold">Both an avatar and a voice are required</span> to generate the
          Video Avatar Summary in AdvicePrep.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-2 items-start">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Avatar</h3>
              <p className="text-sm text-gray-500">The face used in your AdvicePrep videos.</p>
            </div>
            <MediaStatusTag has={!!media?.avatar?.hasAvatar} status={null} />
          </div>
          <AvatarSettingsPanel />
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Voice</h3>
              <p className="text-sm text-gray-500">The cloned voice that narrates your AdvicePrep videos.</p>
            </div>
            <MediaStatusTag has={!!media?.voice} status={media?.voice?.status ?? null} />
          </div>
          <VoiceSettingsPanel />
        </section>
      </div>
    </div>
  )
}
